import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import logger from './utils/logger';

// Only English in main bundle to reduce TBT (Total Blocking Time)
import enTranslations from './locales/en.json';

const SUPPORTED_LANGS = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ja'] as const;
type SupportedLng = (typeof SUPPORTED_LANGS)[number];
const loadedLangs = new Set<string>(['en']);

/**
 * Load a locale bundle on demand (reduces initial parse/main-thread work).
 */
export async function loadLocale(lng: string): Promise<void> {
  if (loadedLangs.has(lng)) return;
  const lang = lng.split('-')[0];
  if (!SUPPORTED_LANGS.includes(lang as SupportedLng)) return;
  try {
    const mod = await import(
      /* webpackChunkName: "locale-[request]" */ `./locales/${lang}.json`
    );
    const data = mod.default || mod;
    i18n.addResourceBundle(lang, 'translation', data);
    loadedLangs.add(lang);
  } catch (e) {
    logger.debug('i18n: could not load locale', lang, e);
  }
}

// Initialize with English only; other locales loaded on demand
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
    },
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
    },
    initImmediate: false,
    load: 'languageOnly',
    preload: ['en'],
    keySeparator: '.',
    nsSeparator: ':',
  })
  .then(() => {
    logger.info('🚀 i18n initialized successfully');
    // Load detected/saved language in idle so it doesn't block first paint
    const lng = i18n.language?.split('-')[0];
    if (lng && lng !== 'en' && SUPPORTED_LANGS.includes(lng as SupportedLng)) {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => loadLocale(lng), { timeout: 2000 });
      } else {
        setTimeout(() => loadLocale(lng), 100);
      }
    }
  })
  .catch((error) => {
    logger.error('❌ i18n initialization failed:', error);
  });

export default i18n;
